export const USER_ROLE_RESELLER_ADMIN = 'reseller_admin'
export const USER_ROLE_TENANT_ADMIN = 'tenant_admin'

const ROOT_RESELLER_KEY = '__root__'
const DIRECT_TENANT_KEY = '__direct__'

const hasExpectedRole = (item, expectedRole) => !item.role || item.role === expectedRole

export const buildPlatformOrganizationRows = ({
  platformUserId,
  resellerAdmins = [],
  tenantAdmins = []
}) => {
  const visibleResellers = resellerAdmins.filter((item) => (
    item?.user_id && hasExpectedRole(item, USER_ROLE_RESELLER_ADMIN)
  ))
  const visibleResellerIds = new Set(visibleResellers.map((item) => item.user_id))
  const resellerChildrenByParent = visibleResellers.reduce((childrenByParent, reseller) => {
    const parentId = reseller.created_by_reseller_admin_id || ROOT_RESELLER_KEY
    const children = childrenByParent[parentId] || []
    children.push(reseller)
    childrenByParent[parentId] = children
    return childrenByParent
  }, {})
  const tenantsByReseller = tenantAdmins.reduce((tenantsByParent, tenant) => {
    if (!tenant?.user_id || !hasExpectedRole(tenant, USER_ROLE_TENANT_ADMIN)) {
      return tenantsByParent
    }
    const resellerId = tenant.reseller_admin_id || ''
    if (resellerId && !visibleResellerIds.has(resellerId)) {
      return tenantsByParent
    }
    if (!resellerId && tenant.created_by_user_id !== platformUserId) {
      return tenantsByParent
    }
    const parentId = resellerId || DIRECT_TENANT_KEY
    const tenants = tenantsByParent[parentId] || []
    tenants.push(tenant)
    tenantsByParent[parentId] = tenants
    return tenantsByParent
  }, {})

  const rows = []
  for (const tenant of tenantsByReseller[DIRECT_TENANT_KEY] || []) {
    rows.push({
      type: 'tenant',
      level: 0,
      parentResellerId: '',
      item: tenant,
      canManage: true
    })
  }

  const visitedResellerIds = new Set()
  const walk = (parentId, level) => {
    for (const reseller of resellerChildrenByParent[parentId] || []) {
      if (visitedResellerIds.has(reseller.user_id)) continue
      visitedResellerIds.add(reseller.user_id)
      const childResellers = resellerChildrenByParent[reseller.user_id] || []
      const tenants = tenantsByReseller[reseller.user_id] || []
      rows.push({
        type: 'reseller',
        level,
        parentResellerId: parentId === ROOT_RESELLER_KEY ? '' : parentId,
        item: reseller,
        hasChildren: Boolean(childResellers.length || tenants.length),
        canManage: parentId === ROOT_RESELLER_KEY
      })
      for (const tenant of tenants) {
        rows.push({
          type: 'tenant',
          level: level + 1,
          parentResellerId: reseller.user_id,
          item: tenant,
          canManage: tenant.created_by_user_id === platformUserId
        })
      }
      walk(reseller.user_id, level + 1)
    }
  }
  walk(ROOT_RESELLER_KEY, 0)
  return rows
}

export const getOrganizationTreeGuides = (rows) => {
  const lastChildByParent = new Map()
  rows.forEach((row, index) => {
    if (row.level > 0) lastChildByParent.set(row.parentResellerId, index)
  })

  const ancestors = []
  return rows.map((row, index) => {
    while (ancestors.length && ancestors[ancestors.length - 1].row.level >= row.level) {
      ancestors.pop()
    }
    const parent = ancestors[ancestors.length - 1]?.row
    const hasParent = Boolean(parent && parent.level === row.level - 1 && parent.item.user_id === row.parentResellerId)
    const guides = {
      hasParent,
      isLastChild: hasParent && lastChildByParent.get(row.parentResellerId) === index,
      continuingLevels: hasParent
        ? ancestors.filter(({ row: ancestor, index: ancestorIndex }) => (
          ancestor.level > 0 && lastChildByParent.get(ancestor.parentResellerId) !== ancestorIndex
        )).map(({ row: ancestor }) => ancestor.level)
        : [],
      hasChildren: row.type === 'reseller' && rows[index + 1]?.parentResellerId === row.item.user_id
    }
    if (row.type === 'reseller') ancestors.push({ row, index })
    return guides
  })
}
